import { HttpInterceptorFn } from '@angular/common/http';
import { from, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Firma las llamadas al UX Gateway público (public-api) con HMAC-SHA256 por-frontend, para el
 * choke point del gateway (app/middlewares/public-api-signature.js). Contrato por headers,
 * IDÉNTICO al de auth-ui/customer-mobile-app/back-office (no debe divergir entre frontends):
 *   x-fna-app        clientAppCode (debe existir en applicationInfo.frontends[].name del gateway)
 *   x-fna-date       epoch en ms (Date.now()) — ventana anti-replay
 *   x-fna-signature  HMAC-SHA256(signatureKey, `${app}\n${date}\n${bodyCanonico}`) en hex
 * bodyCanonico = JSON.stringify(body) si el body tiene contenido; '' si vacío (p.ej. GET).
 *
 * Si `environment.publicApiSignature` no está provisionado (clientAppCode/signatureKey vacíos), la
 * interceptor NO firma y deja pasar la petición tal cual — inofensivo mientras el gateway esté en
 * modo monitor. La signatureKey vive en el bundle (no es un secreto fuerte): su propósito es ligar
 * la petición a un frontend conocido + anti-replay, no autenticación criptográfica robusta.
 */

const HDR_APP = 'x-fna-app';
const HDR_DATE = 'x-fna-date';
const HDR_SIG = 'x-fna-signature';

// Rutas del gateway que se firman: public-api y los proxies admin (kratos-admin/hydra-admin/
// kratos-public), que son pre-sesión y se protegen ligándolos a un frontend conocido. El tráfico
// del motor de formularios (forms/*, session/* bajo apiRootPath) NO va aquí: ya se firma en el body.
const SIGNED_GATEWAY_PATHS = ['/api/', '/kratos-admin/', '/hydra-admin/', '/kratos-public/'];

function canonicalBody(body: unknown): string {
  return body && typeof body === 'object' && Object.keys(body as object).length > 0 ? JSON.stringify(body) : '';
}

// Solo firma peticiones al gateway: relativas (mismo origen) o absolutas al host del gateway.
// Excluye absolutas a hosts externos (p.ej. Google Maps) que casualmente contengan `/api/`.
function targetsSignedGateway(url: string): boolean {
  if (!SIGNED_GATEWAY_PATHS.some(p => url.includes(p))) {
    return false;
  }
  if (url.startsWith('/')) {
    return true;
  }
  const server = environment.appConfig?.serverEntryPoint?.server;
  return server ? url.includes(server) : false;
}

async function hmacSha256Hex(key: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey('raw', enc.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(message));
  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export const publicApiSignatureInterceptor: HttpInterceptorFn = (req, next) => {
  const { clientAppCode, signatureKey } = environment.publicApiSignature ?? {};
  if (!clientAppCode || !signatureKey || !targetsSignedGateway(req.url)) {
    return next(req);
  }

  const date = Date.now().toString();
  const stringToSign = `${clientAppCode}\n${date}\n${canonicalBody(req.body)}`;

  return from(hmacSha256Hex(signatureKey, stringToSign)).pipe(
    switchMap(signature =>
      next(
        req.clone({
          setHeaders: {
            [HDR_APP]: clientAppCode,
            [HDR_DATE]: date,
            [HDR_SIG]: signature,
          },
        })
      )
    )
  );
};
